// src/pages/Admin/Adherents/AdherentDetails.jsx

import React, { useEffect, useState } from "react";
import {
  FaPhone,
  FaSms,
  FaEnvelope,
  FaEdit,
  FaSave,
  FaTimes,
  FaSpinner,
} from "react-icons/fa";
import { toast } from "react-toastify";

import { updatePoids } from "../../../services/adherentServices";

function getStatusClass(statut) {
  switch (statut) {
    case "Licencié":
      return "bg-green-300 text-slate-800";
    case "Licencié Hors Club":
      return "bg-green-200 text-slate-800";
    case "Inscrit":
      return "bg-yellow-300 text-slate-900";
    case "Cours d'essai":
      return "bg-red-300 text-slate-900";
    case "Licencié - Arrêt":
      return "bg-slate-300 text-slate-800";
    case "Ancien Licencié et Arrêt":
      return "bg-slate-300 text-slate-800";
    case "Ancien Licencié":
      return "bg-slate-900 text-white";
    case "Non Licencié - Arrêt":
      return "bg-slate-500 text-white";
    default:
      return "bg-slate-200 text-slate-800";
  }
}

const toAbsolute = (u) => {
  if (!u) return "";

  const s = String(u).trim();

  if (
    /^https?:\/\//i.test(s) ||
    s.startsWith("data:") ||
    s.startsWith("blob:")
  ) {
    return s;
  }

  if (!s.startsWith("/uploads")) return s;

  const base = process.env.REACT_APP_API_URL?.replace(/\/$/, "") || "";
  return `${base}${s}`;
};

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("fr-FR") : "—");

const formatPoids = (value) => {
  if (value === null || value === undefined || value === "") return "—";
  return `${String(value).replace(".", ",")} kg`;
};

const Field = ({ label, value }) => (
  <div className="space-y-1">
    <div className="text-[11px] uppercase tracking-wide text-slate-500 dark:text-slate-400">
      {label}
    </div>
    <div className="text-sm text-slate-800 dark:text-slate-100 break-words">
      {value || "—"}
    </div>
  </div>
);

export default function AdherentDetails({
  adherent = {},
  showTeacherNotes = true,
  onEdit,
  onPoidsUpdated,
}) {
  const {
    id,
    photo_url,
    prenom,
    nom,
    statut_inscription,
    tel,
    tel_secondaire,
    email,
    email_secondaire,
    createdAt,
    representant_legal,
    adresse,
    cp,
    commune,
    genre,
    date_naissance,
    lieu_naissance,
    poids,
    licence_number,
    saison_active,
    notes_adherent,
    notes_enseignant,
    passsport_number,
    passclub32_number,
    updatedAt,
  } = adherent || {};

  const adherentId = id || adherent?.adherent_id || adherent?.user_id;

  const [localPoids, setLocalPoids] = useState(poids ?? "");
  const [poidsInput, setPoidsInput] = useState(poids ?? "");
  const [isEditingPoids, setIsEditingPoids] = useState(false);
  const [savingPoids, setSavingPoids] = useState(false);

  useEffect(() => {
    setLocalPoids(poids ?? "");
    setPoidsInput(poids ?? "");
    setIsEditingPoids(false);
  }, [poids, adherentId]);

  const hasTel = !!tel;
  const hasEmail = !!email;

  const safePhoto = toAbsolute(photo_url);
  const absPhoto = safePhoto || "/img/user_avatar.jpg";
  const cacheBust = updatedAt ? `?v=${new Date(updatedAt).getTime()}` : "";

  const openPoidsEditor = () => {
    setPoidsInput(localPoids ?? "");
    setIsEditingPoids(true);
  };

  const cancelPoidsEditor = () => {
    setPoidsInput(localPoids ?? "");
    setIsEditingPoids(false);
  };

  const handlePoidsSubmit = async (e) => {
    e.preventDefault();

    if (!adherentId) {
      toast.error("Impossible de modifier le poids : adhérent introuvable.");
      return;
    }

    const cleanValue = String(poidsInput ?? "").trim().replace(",", ".");

    if (!cleanValue) {
      toast.error("Merci de renseigner un poids.");
      return;
    }

    const numericPoids = Number(cleanValue);

    if (Number.isNaN(numericPoids)) {
      toast.error("Le poids saisi n'est pas valide.");
      return;
    }

    if (numericPoids <= 0 || numericPoids > 250) {
      toast.error("Merci de saisir un poids cohérent.");
      return;
    }

    try {
      setSavingPoids(true);

      await updatePoids(adherentId, numericPoids);

      setLocalPoids(numericPoids);
      setPoidsInput(numericPoids);
      setIsEditingPoids(false);

      if (typeof onPoidsUpdated === "function") {
        onPoidsUpdated({
          ...adherent,
          poids: numericPoids,
        });
      }

      toast.success("Poids mis à jour.");
    } catch (error) {
      console.error("Erreur updatePoids :", error);
      toast.error(
        error?.response?.data?.message ||
          "Erreur lors de la mise à jour du poids."
      );
    } finally {
      setSavingPoids(false);
    }
  };

  return (
    <div className="rounded-2xl overflow-hidden ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-white/90 dark:bg-slate-900/60 backdrop-blur">
      {/* Header dégradé */}
      <div className="bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500 px-5 py-4">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-white/75">
              Fiche adhérent
            </p>
            <h2 className="mt-1 text-white text-lg sm:text-xl font-bold">
              {String(nom || "").toUpperCase()} {prenom || ""}
            </h2>
          </div>

          <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
            <span
              className={`inline-flex items-center justify-center px-3 py-2 rounded-xl text-xs font-bold shadow ${getStatusClass(
                statut_inscription
              )}`}
              title="Statut d'inscription"
            >
              {statut_inscription || "—"}
            </span>

            {onEdit && (
              <button
                type="button"
                onClick={() => onEdit(adherent)}
                title="Modifier la fiche adhérent"
                className="inline-flex min-h-[46px] items-center justify-center gap-2 rounded-xl bg-white px-4 py-2 text-sm font-bold text-slate-900 shadow-lg ring-1 ring-white/70 transition hover:bg-slate-50 active:scale-[0.98]"
              >
                <FaEdit className="text-base text-blue-600" />
                Modifier la fiche
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Contenu */}
      <div className="p-5 sm:p-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          {/* Colonne gauche : photo + actions + poids */}
          <div className="lg:col-span-1">
            <div className="rounded-xl overflow-hidden ring-1 ring-slate-200 dark:ring-slate-800 bg-white dark:bg-slate-900">
              <div className="mx-auto w-full max-w-64 h-64">
                <img
                  src={`${absPhoto}${cacheBust}`}
                  alt={`${prenom || ""} ${nom || ""}`}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.currentTarget.onerror = null;
                    e.currentTarget.src = "/img/user_avatar.jpg";
                  }}
                />
              </div>
            </div>

            {/* Actions rapides */}
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-1 gap-2">
              <a
                href={hasTel ? `tel:${tel}` : undefined}
                aria-disabled={!hasTel}
                onClick={(e) => {
                  if (!hasTel) e.preventDefault();
                }}
                className={`inline-flex min-h-[46px] items-center justify-center gap-2 rounded-xl px-3 py-2 text-sm font-bold ring-1 shadow-sm transition
                  ${
                    hasTel
                      ? "bg-blue-600 text-white hover:bg-blue-700 ring-blue-300/40"
                      : "bg-slate-200 text-slate-500 ring-slate-300/40 cursor-not-allowed"
                  }`}
              >
                <FaPhone /> Appel
              </a>

              <a
                href={hasTel ? `sms:${tel}` : undefined}
                aria-disabled={!hasTel}
                onClick={(e) => {
                  if (!hasTel) e.preventDefault();
                }}
                className={`inline-flex min-h-[46px] items-center justify-center gap-2 rounded-xl px-3 py-2 text-sm font-bold ring-1 shadow-sm transition
                  ${
                    hasTel
                      ? "bg-emerald-600 text-white hover:bg-emerald-700 ring-emerald-300/40"
                      : "bg-slate-200 text-slate-500 ring-slate-300/40 cursor-not-allowed"
                  }`}
              >
                <FaSms /> SMS
              </a>

              <a
                href={hasEmail ? `mailto:${email}` : undefined}
                aria-disabled={!hasEmail}
                onClick={(e) => {
                  if (!hasEmail) e.preventDefault();
                }}
                className={`inline-flex min-h-[46px] items-center justify-center gap-2 rounded-xl px-3 py-2 text-sm font-bold ring-1 shadow-sm transition
                  ${
                    hasEmail
                      ? "bg-indigo-600 text-white hover:bg-indigo-700 ring-indigo-300/40"
                      : "bg-slate-200 text-slate-500 ring-slate-300/40 cursor-not-allowed"
                  }`}
              >
                <FaEnvelope /> Mail
              </a>
            </div>

            {/* Modification rapide du poids */}
            <div className="mt-4 rounded-2xl bg-blue-50 p-4 ring-1 ring-blue-200 dark:bg-blue-950/30 dark:ring-blue-800/70">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-[11px] uppercase tracking-wide text-blue-700 dark:text-blue-300 font-bold">
                    Pesée début de saison
                  </div>
                  <div className="mt-1 text-sm text-slate-600 dark:text-slate-300">
                    Poids actuel
                  </div>
                </div>

                <div className="rounded-xl bg-white px-3 py-2 text-lg font-bold text-blue-700 shadow-sm ring-1 ring-blue-100 dark:bg-slate-900 dark:text-blue-300 dark:ring-blue-900">
                  {formatPoids(localPoids)}
                </div>
              </div>

              {!isEditingPoids ? (
                <button
                  type="button"
                  onClick={openPoidsEditor}
                  className="mt-4 inline-flex min-h-[48px] w-full items-center justify-center gap-2 rounded-xl bg-blue-600 px-4 py-3 text-sm font-bold text-white shadow-sm transition hover:bg-blue-700 active:scale-[0.98]"
                >
                  <FaEdit />
                  Modifier le poids
                </button>
              ) : (
                <form onSubmit={handlePoidsSubmit} className="mt-4 space-y-3">
                  <div>
                    <label
                      htmlFor="poids-adherent"
                      className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-600 dark:text-slate-300"
                    >
                      Nouveau poids en kg
                    </label>

                    <div className="flex items-center gap-2">
                      <input
                        id="poids-adherent"
                        type="number"
                        inputMode="decimal"
                        min="1"
                        max="250"
                        step="0.1"
                        value={poidsInput}
                        onChange={(e) => setPoidsInput(e.target.value)}
                        placeholder="Ex : 34.5"
                        autoFocus
                        className="min-h-[50px] w-full rounded-xl border border-blue-200 bg-white px-4 text-lg font-bold text-slate-900 outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-200 dark:border-blue-900 dark:bg-slate-950 dark:text-white dark:focus:ring-blue-900/50"
                      />

                      <span className="text-sm font-bold text-slate-500 dark:text-slate-300">
                        kg
                      </span>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={cancelPoidsEditor}
                      disabled={savingPoids}
                      className="inline-flex min-h-[48px] items-center justify-center gap-2 rounded-xl bg-white px-3 py-2 text-sm font-bold text-slate-700 ring-1 ring-slate-200 transition hover:bg-slate-50 disabled:opacity-60 dark:bg-slate-900 dark:text-slate-100 dark:ring-slate-700"
                    >
                      <FaTimes />
                      Annuler
                    </button>

                    <button
                      type="submit"
                      disabled={savingPoids}
                      className="inline-flex min-h-[48px] items-center justify-center gap-2 rounded-xl bg-emerald-600 px-3 py-2 text-sm font-bold text-white shadow-sm transition hover:bg-emerald-700 disabled:opacity-60"
                    >
                      {savingPoids ? (
                        <>
                          <FaSpinner className="animate-spin" />
                          Enregistrement
                        </>
                      ) : (
                        <>
                          <FaSave />
                          Enregistrer
                        </>
                      )}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>

          {/* Colonne droite : infos */}
          <div className="lg:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-5">
            {/* Coordonnées */}
            <div className="md:col-span-2">
              <div className="mb-2 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <div className="text-lg font-bold uppercase tracking-wide text-slate-600 dark:text-slate-300">
                  Coordonnées
                </div>

                {onEdit && (
                  <button
                    type="button"
                    onClick={() => onEdit(adherent)}
                    title="Modifier"
                    className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-xl border border-blue-200 bg-blue-50 px-4 py-2 text-sm font-bold text-blue-700 transition hover:bg-blue-100 dark:border-blue-800 dark:bg-blue-950/30 dark:text-blue-300 dark:hover:bg-blue-950/50"
                  >
                    <FaEdit />
                    Éditer l’adhérent
                  </button>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 rounded-xl ring-1 ring-slate-200 dark:ring-slate-800 p-4 bg-slate-100 dark:bg-slate-900/40">
                <Field label="Email" value={email} />
                <Field label="Email secondaire" value={email_secondaire} />
                <Field label="Téléphone" value={tel} />
                <Field label="Téléphone secondaire" value={tel_secondaire} />
                <Field
                  label="Adresse"
                  value={[adresse, cp, commune].filter(Boolean).join(" ") || "—"}
                />
                <Field label="Représentant légal" value={representant_legal} />
              </div>
            </div>

            {/* Informations personnelles */}
            <div className="md:col-span-1">
              <div className="text-xs font-bold uppercase tracking-wide text-slate-600 dark:text-slate-300 mb-2">
                Informations personnelles
              </div>

              <div className="grid grid-cols-1 gap-4 rounded-xl ring-1 ring-slate-200 dark:ring-slate-800 p-4 bg-slate-100 dark:bg-slate-900/40">
                <Field label="Genre" value={genre} />
                <Field label="Date de naissance" value={fmtDate(date_naissance)} />
                <Field label="Lieu de naissance" value={lieu_naissance} />
                <Field label="Nationalité Française" value="France" />
                <Field label="Poids" value={formatPoids(localPoids)} />
                <Field label="Créé le" value={fmtDate(createdAt)} />
              </div>
            </div>

            {/* Licence & Club */}
            <div className="md:col-span-1">
              <div className="text-xs font-bold uppercase tracking-wide text-slate-600 dark:text-slate-300 mb-2">
                Licence & club
              </div>

              <div className="grid grid-cols-1 gap-4 rounded-xl ring-1 ring-slate-200 dark:ring-slate-800 p-4 bg-slate-100 dark:bg-slate-900/40">
                <Field label="N° de licence" value={licence_number} />
                <Field label="Saison active" value={saison_active} />
                <Field label="N° Pass'Sport" value={passsport_number} />
                <Field label="N° Pass'Club 32" value={passclub32_number} />
              </div>
            </div>

            {/* Notes */}
            <div className="md:col-span-2">
              <div className="text-xs font-bold uppercase tracking-wide text-slate-600 dark:text-slate-300 mb-2">
                Notes
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="rounded-xl ring-1 ring-slate-200 dark:ring-slate-800 p-4 bg-slate-100 dark:bg-slate-900/40">
                  <div className="text-[11px] uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-1">
                    Notes adhérent
                  </div>

                  <div className="text-sm text-slate-800 dark:text-slate-100 whitespace-pre-wrap">
                    {notes_adherent || "—"}
                  </div>
                </div>

                {showTeacherNotes && (
                  <div className="rounded-xl ring-1 ring-slate-200 dark:ring-slate-800 p-4 bg-slate-100 dark:bg-slate-900/40">
                    <div className="text-[11px] uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-1">
                      Notes enseignant
                    </div>

                    <div className="text-sm text-slate-800 dark:text-slate-100 whitespace-pre-wrap">
                      {notes_enseignant || "—"}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}